/**
 * ScanStatus - Shows job status for Assets and Vulnerabilities
 *
 * Uses shared ScanStatus components for unified UI across Asset,
 * Vulnerability, and Capability drawers.
 *
 * For Application assets, fetches jobs across all member assets
 * (IPs + WebApplications) via a single DynamoDB multi-query so the
 * indicator and job list reflect the full application surface.
 *
 * Performance optimization: Uses useJobsBySource for vulnerability context
 * to avoid waterfall API pattern (1 call vs 2 sequential).
 */
import { useMemo, useState } from 'react';

import {
  getScanStatusConfig,
  ScanStatusBox,
  ScanStatusModal,
} from '@/components/ScanStatus';
import { useAIAgents } from '@/hooks/useAIAgents';
import { useApplicationJobs } from '@/hooks/useApplicationJobs';
import { useCancelJob, useJobs, useReRunJob } from '@/hooks/useJobs';
import { useJobsBySource } from '@/hooks/useJobsBySource';
import { stripJobSourceTimestamp } from '@/sections/jobs/utils';
import { Asset, Job, JobStatus, Risk } from '@/types';
import { mergeJobStatus } from '@/utils/api';

interface ScanStatusProps {
  asset?: Asset;
  risk?: Risk;
}

const RUNNING_STATUSES = new Set<string>([
  JobStatus.Running,
  JobStatus.AsyncRunning,
  JobStatus.AsyncReturning,
  JobStatus.Queued,
]);

export function ScanStatus({ asset, risk }: ScanStatusProps) {
  const [open, setOpen] = useState(false);
  const { mutateAsync: reRunJob } = useReRunJob();
  const { mutateAsync: cancelJob } = useCancelJob();
  const { data: aiAgents } = useAIAgents();

  const agentCapabilityNames = useMemo(() => {
    if (!aiAgents) return new Set<string>();
    return new Set(aiAgents.filter(a => a.capability).map(a => a.name));
  }, [aiAgents]);

  const isApplication = asset?.class === 'application';
  const hasParentApp = Boolean(asset?.group_key);
  const useAppJobs = isApplication || hasParentApp;

  // Use dedicated hook for vulnerability context (single API call)
  const {
    data: riskJobs,
    status: riskJobsStatus,
    error: riskJobsError,
    refetch: refetchRiskJobs,
  } = useJobsBySource(risk?.source);

  // Single-asset jobs (fallback when asset has no parent application)
  const {
    data: assetJobs,
    status: assetJobsStatus,
    error: assetJobsError,
    refetch: refetchAssetJobs,
  } = useJobs({
    query:
      asset && !useAppJobs ? `#job#${asset.group}#${asset.identifier}` : '',
    search: '',
    failedReason: '',
  });

  // Application-wide jobs: fetches jobs across all members of the parent
  // application (whether the current asset IS the application or belongs to one)
  const {
    data: appJobs,
    status: appJobsStatus,
    error: appJobsError,
    refetch: refetchAppJobs,
  } = useApplicationJobs(useAppJobs ? asset : undefined);

  // Select appropriate data based on context
  const jobs: Job[] = risk ? riskJobs : useAppJobs ? appJobs : assetJobs;
  const jobsStatus = risk
    ? riskJobsStatus
    : useAppJobs
      ? appJobsStatus
      : assetJobsStatus;
  const jobsError = risk
    ? riskJobsError
    : useAppJobs
      ? appJobsError
      : assetJobsError;
  const refetch = risk
    ? refetchRiskJobs
    : useAppJobs
      ? refetchAppJobs
      : refetchAssetJobs;

  const scanStatus = useMemo(() => {
    if (!jobs.length) return undefined;
    return mergeJobStatus(jobs.map(job => job.status.slice(0, 2) as JobStatus));
  }, [jobs]);

  const statusTooltip = useMemo(() => {
    if (scanStatus !== JobStatus.Partial) return undefined;
    const statuses = jobs.map(job => job.status.slice(0, 2) as JobStatus);
    const passCount = statuses.filter(s => s === JobStatus.Pass).length;
    const failCount = statuses.filter(s => s === JobStatus.Fail).length;
    return `${passCount} passed, ${failCount} failed`;
  }, [scanStatus, jobs]);

  const hasAgentRunning = useMemo(() => {
    if (!agentCapabilityNames.size) return false;
    return jobs.some(
      job =>
        RUNNING_STATUSES.has(job.status.slice(0, 2)) &&
        agentCapabilityNames.has(stripJobSourceTimestamp(job.source))
    );
  }, [jobs, agentCapabilityNames]);

  const statusConfig = useMemo(() => {
    const config = getScanStatusConfig(scanStatus);
    if (!config) return config;
    const withTooltip = statusTooltip
      ? { ...config, tooltip: statusTooltip }
      : config;
    if (
      hasAgentRunning &&
      (scanStatus === JobStatus.Running ||
        scanStatus === JobStatus.AsyncRunning ||
        scanStatus === JobStatus.AsyncReturning ||
        scanStatus === JobStatus.Queued)
    ) {
      return { ...withTooltip, label: 'Agent Running' };
    }
    return withTooltip;
  }, [scanStatus, statusTooltip, hasAgentRunning]);

  const actionHandlers = useMemo(
    () => ({
      handleRerun: (jobKey: string) => reRunJob({ jobKey }),
      handleCancel: (jobKey: string) => cancelJob({ jobKey }),
    }),
    [reRunJob, cancelJob]
  );

  const modalTitle = asset
    ? isApplication
      ? `${asset.group || asset.identifier} Scans`
      : `${asset.identifier} Scans`
    : risk
      ? `${risk.name} Scans`
      : 'Scans';

  return (
    <>
      <ScanStatusBox
        statusConfig={statusConfig}
        isLoading={jobsStatus === 'pending'}
        onViewDetails={() => setOpen(true)}
        disabled={!scanStatus}
      />
      <ScanStatusModal
        open={open}
        onClose={() => setOpen(false)}
        title={modalTitle}
        jobs={jobs}
        isLoading={jobsStatus === 'pending'}
        error={jobsError}
        refetch={refetch}
        actionHandlers={actionHandlers}
      />
    </>
  );
}
